import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
  } from "react";
  import {
    cartApi,
    CartHeader,
    CartItem,
    CartSummary,
    Identify,
    UpsertItemPayload,
  } from "../../api/cart";
  import { useCustomerAuth } from "../CustomerAuthContext/CustomerAuthContext";
  
  // Basit uuid
  const uuid = () => {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };
  
  export type CartState = {
    // Data
    header?: CartHeader;
    items: CartItem[];
    totals?: CartSummary["totals"];
    count: number;
    currency: string;
    loading: boolean;
    error?: string | null;
  
    // Kimlik
    visitorKey: string;
  
    // UI (mini sepet / sheet)
    isOpen: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;
  
    // Actions
    refresh: () => Promise<void>;
    summary: () => Promise<void>; // alias
    addItem: (p: Omit<UpsertItemPayload, keyof Identify>) => Promise<boolean>;
    changeQty: (sepetKalemId: number, adet: number) => Promise<boolean>;
    removeItem: (sepetKalemId: number) => Promise<boolean>;
    clear: () => Promise<boolean>;
    clearCart: () => Promise<boolean>; // alias
    setCoupon: (code: string) => Promise<boolean>;
  };
  
  const CartContext = createContext<CartState | undefined>(undefined);
  
  export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { customer } = useCustomerAuth();
  
    // Data state
    const [header, setHeader] = useState<CartHeader | undefined>();
    const [items, setItems] = useState<CartItem[]>([]);
    const [totals, setTotals] = useState<CartSummary["totals"] | undefined>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
  
    // UI state (mini-cart / sheet)
    const [isOpen, setIsOpen] = useState(false);
    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const toggle = useCallback(() => setIsOpen((s) => !s), []);
  
    // Visitor key
    const [visitorKey] = useState<string>(() => {
      const ls = localStorage.getItem("visitor_key");
      if (ls) return ls;
      const k = `v_${uuid()}`;
      localStorage.setItem("visitor_key", k);
      return k;
    });
  
    // login sonrası bir kere misafir sepetini kullanıcıya bağla
    const attachedRef = useRef(false);
    useEffect(() => {
      (async () => {
        if (customer?.id && visitorKey && !attachedRef.current) {
          try {
            await cartApi.attachGuestToMember({
              musteriId: customer.id,
              ziyaretciAnahtari: visitorKey,
            });
            attachedRef.current = true;
            await ensureActive();
            await refresh();
          } catch {
            /* misafir sepeti olmayabilir: sessiz geç */
          }
        }
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer?.id]);
  
    const identify: Identify = useMemo(
      () => (customer?.id ? { musteriId: customer.id } : { ziyaretciAnahtari: visitorKey }),
      [customer?.id, visitorKey]
    );
  
    const ensureActive = useCallback(async () => {
      const res = await cartApi.ensureActive({
        musteriId: customer?.id,
        ziyaretciAnahtari: customer?.id ? undefined : visitorKey,
        paraBirimiKodu: "TRY",
      });
      if (res.success && res.data) setHeader(res.data);
      else throw new Error(res.errorDetail || "Sepet oluşturulamadı");
    }, [customer?.id, visitorKey]);
  
    const refresh = useCallback(async () => {
      setLoading(true);
      setError(null);
      try {
        if (!header) await ensureActive();
        const res = await cartApi.summary(identify);
        if (!res.success || !res.data) throw new Error(res.errorDetail || "Sepet alınamadı");
        setHeader(res.data.header);
        setItems(res.data.items);
        setTotals(res.data.totals);
      } catch (e: any) {
        setError(e?.message || "Hata");
      } finally {
        setLoading(false);
      }
    }, [header, identify, ensureActive]);
  
    // İlk yükleme
    useEffect(() => {
      refresh();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visitorKey, customer?.id]);
  
    const addItem: CartState["addItem"] = async (p) => {
      try {
        setLoading(true);
        setError(null);
        if (!header) await ensureActive();
        const res = await cartApi.upsertItem({ ...identify, ...p });
        if (!res.success) throw new Error(res.errorDetail || "Sepete eklenemedi");
        await refresh();
        // Ürün eklenince mini sepeti aç
        open();
        return true;
      } catch (e: any) {
        setError(e?.message || "Hata");
        return false;
      } finally {
        setLoading(false);
      }
    };
  
    const changeQty: CartState["changeQty"] = async (sepetKalemId, adet) => {
      try {
        setLoading(true);
        const res = await cartApi.changeQty({ ...identify, sepetKalemId, adet });
        if (!res.success) throw new Error(res.errorDetail || "Adet güncellenemedi");
        await refresh();
        return true;
      } catch {
        return false;
      } finally {
        setLoading(false);
      }
    };
  
    const removeItem: CartState["removeItem"] = async (sepetKalemId) => {
      try {
        setLoading(true);
        const res = await cartApi.deleteItem({ ...identify, sepetKalemId });
        if (!res.success) throw new Error(res.errorDetail || "Satır silinemedi");
        await refresh();
        return true;
      } catch {
        return false;
      } finally {
        setLoading(false);
      }
    };
  
    const clear = async () => {
      try {
        setLoading(true);
        const res = await cartApi.clear(identify);
        if (!res.success) throw new Error(res.errorDetail || "Sepet temizlenemedi");
        await refresh();
        return true;
      } catch {
        return false;
      } finally {
        setLoading(false);
      }
    };
  
    // alias & ekler
    const summary = useCallback(async () => refresh(), [refresh]);
    const clearCart = useCallback(async () => clear(), [clear]);
  
    const setCoupon: CartState["setCoupon"] = async (code) => {
      try {
        setLoading(true);
        const res = await cartApi.setCoupon({ ...identify, indirimKodu: code });
        if (!res.success) throw new Error(res.errorDetail || "Kupon uygulanamadı");
        await refresh();
        return true;
      } catch {
        return false;
      } finally {
        setLoading(false);
      }
    };
  
    const value: CartState = {
      // data
      header,
      items,
      totals,
      count: items.reduce((s, it) => s + it.adet, 0),
      currency: header?.paraBirimiKodu || "TRY",
      loading,
      error,
  
      // kimlik
      visitorKey,
  
      // ui
      isOpen,
      open,
      close,
      toggle,
  
      // actions
      refresh,
      summary,
      addItem,
      changeQty,
      removeItem,
      clear,
      clearCart,
      setCoupon,
    };
  
    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
  };
  
  export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
  };
  